import {
  ComponentProps,
  FC,
  PropsWithChildren,
  useCallback,
  useState,
} from 'react';
import {
  ActionsMenu,
  Alert,
  Loader,
  NothingFoundAlert,
  PageContentLayout,
  PageHeaderLayout,
  SideMenu,
  Typography,
} from 'melp-design/components';
import { Box, Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { ROUTES } from 'config';
import { useClient } from 'state/Clients';
import { useRouteParams } from 'utils/useIdParam';
import { useDialog } from 'utils/Dialog';
import FormDialog from 'components/dialogs/FormDialog/FormDialog';
import { UnpackNestedValue } from 'react-hook-form';
import { ImportClientError } from 'types/Clients';
import { useUploadClientCSVForm } from 'configs/Forms/UploadClientCSVForm';
import { usePredefinedToasts } from 'utils/Toast';
import { useConfirm } from 'store/confirm';
import { useAdminClient, useAdminDeleteClient } from 'store/admin-clients';
import { useSearchParams } from 'utils/navigation';
import { adminClientDepositsFiltersSchema } from 'store/admin-client-deposits';
import moment from 'moment';

export const AdminClientLayout: FC<PropsWithChildren<{}>> = ({ children }) => {
  const { t } = useTranslation();
  const { path } = useRouteMatch();
  const { id: clientId } = useRouteParams(
    ROUTES.admin.clients.details.settings,
  );
  const predefinedToasts = usePredefinedToasts();
  const history = useHistory();
  const { confirmation } = useConfirm();
  const { stringifyParams } = useSearchParams(adminClientDepositsFiltersSchema);

  const { closeDialog, openDialog, open } = useDialog();
  const uploadFormDialogForm = useUploadClientCSVForm();

  const [importErrors, setImportErrors] = useState<ImportClientError[]>([]);

  const { data: client, isLoading } = useAdminClient(clientId);
  const { importClients } = useClient(clientId);

  const { mutate: deleteClient, isLoading: isDeleting } =
    useAdminDeleteClient();

  const submitForm = useCallback(
    async (data: UnpackNestedValue<{}>) => {
      const dataType = data as { csvFile: File };
      const file = dataType.csvFile;
      const resultData = await importClients(file);
      if (resultData?.errors?.length > 0) {
        setImportErrors(resultData?.errors);
      } else {
        setImportErrors([]);
      }
      closeDialog();
    },
    [importClients, closeDialog],
  );

  const criticalErrors = importErrors.filter(
    (error) => error.severity === 'critical',
  );
  const warningErrors = importErrors.filter(
    (error) => error.severity === 'warning',
  );
  const clearImportErrors = () => setImportErrors([]);

  const tabs: ComponentProps<typeof SideMenu>['tabs'] = [
    {
      key: 'settings',
      label: t('sidebar.companySettingsGeneralSettings'),
      url: ROUTES.admin.clients.details.settings.replace(':id', clientId),
      isActive: path === ROUTES.admin.clients.details.settings,
    },
    {
      key: 'administrators',
      label: t('sidebar.administrators'),
      url: ROUTES.admin.clients.details.administrators.list.replace(
        ':id',
        clientId,
      ),
      isActive: path === ROUTES.admin.clients.details.administrators.list,
    },
    {
      key: 'companies',
      label: t('sidebar.companies'),
      url: ROUTES.admin.clients.details.companies.list.replace(':id', clientId),
      isActive: path === ROUTES.admin.clients.details.companies.list,
    },
    {
      key: 'features',
      label: t('sidebar.features'),
      url: ROUTES.admin.clients.details.features.replace(':id', clientId),
      isActive: path === ROUTES.admin.clients.details.features,
    },
    {
      key: 'deposits',
      label: t('deposits.deposit_balance.title'),
      url: `${ROUTES.admin.clients.details.deposits.list.replace(
        ':id',
        clientId,
      )}${stringifyParams({
        params: {
          date: moment()
            .subtract(1, 'month')
            .endOf('month')
            .toISOString(true)
            .split('T')[0],
        },
      })}`,
      isActive: path === ROUTES.admin.clients.details.deposits.list,
    },
    {
      key: 'deposit-history',
      label: t('clients.deposit_management.title'),
      url: ROUTES.admin.clients.details.deposits.history.replace(
        ':id',
        clientId,
      ),
      isActive: path === ROUTES.admin.clients.details.deposits.history,
    },
    {
      key: 'transmission-acts',
      label: t('transmissionActs.title', { count: 2 }),
      url: ROUTES.admin.clients.details.transmissionActs.list.replace(
        ':id',
        clientId,
      ),
      isActive: path === ROUTES.admin.clients.details.transmissionActs.list,
    },
  ];

  if (isLoading) return <Loader />;
  if (!client) return <NothingFoundAlert />;

  return (
    <>
      <Stack gap={2}>
        <PageHeaderLayout
          breadcrumbs={[
            {
              label: t('menu.clients'),
              url: ROUTES.admin.clients.list,
            },
            {
              label: client?.name ?? '',
            },
          ]}
          side={
            <ActionsMenu
              items={[
                {
                  label: t('actions.import'),
                  onClick: () => {
                    openDialog();
                  },
                },
                {
                  label: t('actions.delete'),
                  onClick: async () => {
                    const { confirmed } = await confirmation(
                      t('clients.deleteConfirmationContent'),
                      t('clients.deleteConfirmationTitle'),
                      'danger',
                    );
                    if (confirmed) {
                      deleteClient(
                        { clientId },
                        {
                          onSuccess: () => {
                            predefinedToasts.success.deleted();
                            history.push(ROUTES.admin.clients.list);
                          },
                        },
                      );
                    }
                  },
                  color: 'error',
                },
              ]}
            />
          }
        />

        <PageContentLayout sidebar={<SideMenu tabs={tabs} />}>
          <Stack gap={2}>
            {children}

            {!!criticalErrors.length && (
              <Alert severity="error" onClose={clearImportErrors}>
                <Stack gap="10px">
                  {criticalErrors.map((error, i) => (
                    <Box key={i}>
                      <Typography variant="h3" color="error" component="span">
                        {`Error in row "${error.rowNumber}" field "${error.fieldName}": `}
                      </Typography>
                      <Typography variant="p1" color="error" component="span">
                        {error.message}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              </Alert>
            )}
            {!!warningErrors.length && (
              <Box mt="20px">
                <Alert severity="warning" onClose={clearImportErrors}>
                  <Stack gap="10px">
                    {warningErrors.map((error, i) => (
                      <Box key={i}>
                        {!!error.rowNumber && !!error.fieldName && (
                          <Typography
                            variant="h3"
                            color="warning"
                            component="span"
                          >
                            {`Warning in row "${error.rowNumber}" field "${error.fieldName}": `}
                          </Typography>
                        )}
                        <Typography
                          variant="p1"
                          color="warning"
                          component="span"
                        >
                          {error.message}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                </Alert>
              </Box>
            )}
          </Stack>
        </PageContentLayout>
      </Stack>

      <FormDialog
        onCancelClick={closeDialog}
        onSubmitForm={submitForm}
        id="create-employee-dialog"
        open={open}
        title={t('clients.import_employees')}
        actionLabel={t('actions.import')}
        form={uploadFormDialogForm}
        loading={isDeleting}
      />
    </>
  );
};
